import { useMutation, useQueryClient } from "@enkor/react-query";

import BaseApi from "@logics/api/baseApi";
import useModalState from "@logics/hooks/useModalState";
import {
  RequestCreateProject,
  RequestCreateProjectCancel,
  RequestCreateProjectComment,
  RequestCreateProjectCommentReply,
  RequestEventCancel,
  RequestUpdateProject,
  RequestUpdateProjectComment,
  RequestUpdateProjectCommentReply,
  ResponseCreateProject,
} from "@models/data-contracts";
import { ApiResponseDto } from "@models/client";

import { projectQueryKeys } from "../constants/projectQueryKeys";
import { projectMutationKeys } from "../constants/projectMutationKeys";

const projectApi = new BaseApi("/project");
const eventApi = new BaseApi("/event");
const eventApiV2 = new BaseApi("/event", "v2");

// ------------------------------------------------------------------------

export const useCreateProjectMutation = () => {
  return useMutation({
    mutationFn: async (body: RequestCreateProject) => {
      const { data } = await projectApi.post<
        ApiResponseDto<ResponseCreateProject>
      >("", body);

      return data;
    },
  });
};

// ------------------------------------------------------------------------

export const useUpdateProjectMutation = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      ...body
    }: RequestUpdateProject & { projectId: string }) =>
      projectApi.patch(`/${projectId}`, body),
  });
};

// ------------------------------------------------------------------------

export const useCreateProjectRegisterMutation = () => {
  return useMutation({
    mutationFn: (projectId: string) => projectApi.post(`${projectId}/join`),
  });
};

// ------------------------------------------------------------------------

export const useCreateProjectUnregisterMutation = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      content,
    }: RequestCreateProjectCancel & {
      projectId: string;
    }) =>
      projectApi.post(`${projectId}/leave`, {
        content,
      }),
  });
};

// ------------------------------------------------------------------------

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: projectMutationKeys.mutateProjectComments(["CREATE"]),
    mutationFn: ({
      projectId,
      content,
    }: RequestCreateProjectComment & { projectId: string }) =>
      projectApi.post(`/${projectId}/comment`, { content }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectComments({ projectId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useUpdateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: projectMutationKeys.mutateProjectComments(["UPDATE"]),
    mutationFn: ({
      projectId,
      commentId,
      content,
    }: RequestUpdateProjectComment & {
      projectId: string;
      commentId: number;
    }) => projectApi.patch(`/${projectId}/comment/${commentId}`, { content }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectComments({ projectId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationFn: ({
      projectId,
      commentId,
    }: {
      projectId: string;
      commentId: number;
    }) => projectApi.delete(`/${projectId}/comment/${commentId}`),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectComments({ projectId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useCreateReplyMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: projectMutationKeys.mutateCommentReplies(["CREATE"]),
    mutationFn: ({
      projectId,
      commentId,
      content,
    }: RequestCreateProjectCommentReply & {
      projectId: string;
      commentId: number;
    }) =>
      projectApi.post(`/${projectId}/comment/${commentId}/reply`, { content }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectComments({ projectId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useUpdateReplyMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: projectMutationKeys.mutateCommentReplies(["UPDATE"]),
    mutationFn: ({
      projectId,
      commentId,
      replyId,
      content,
    }: RequestUpdateProjectCommentReply & {
      projectId: string;
      commentId: number;
      replyId: number;
    }) =>
      projectApi.patch(`/${projectId}/comment/${commentId}/reply/${replyId}`, {
        content,
      }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectComments({ projectId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useDeleteReplyMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationFn: ({
      projectId,
      commentId,
      replyId,
    }: {
      projectId: string;
      commentId: number;
      replyId: number;
    }) =>
      projectApi.delete(`/${projectId}/comment/${commentId}/reply/${replyId}`),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectComments({ projectId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

//INFO: 에디션 등록시 채팅을 위한 이벤트 참여
export const useCreateEventRegisterMutation = () => {
  return useMutation({
    mutationFn: (eventId: string) => eventApi.post(`${eventId}/join`),
  });
};

// ------------------------------------------------------------------------

// INFO: 에디션 취소시 채팅을 위한 이벤트 취소
export const useCreateEventUnregisterMutation = () => {
  return useMutation({
    mutationFn: ({
      eventId,
      reason,
    }: RequestEventCancel & {
      eventId: string;
    }) => eventApiV2.post(`${eventId}/leave`, { reason }),
  });
};
